import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';

import { Link, SpacerVertical, SpacerHorizontal, Typography } from '@klarna/bubble-ui';

import { translator } from 'mage-i18n';
import { components as clientComponents } from 'mage-client';
import { selectors as configSelectors } from 'mage-config';
import { HorizontalWrapper, defaultContainerPropTypes } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { ContainerTemplate, CallToActionButton, SecondaryButton } from '@/components';
import { CAPS } from '@/constants';
import { CY_CONFIRM_COPY_STEP_CONTAINER_IDS } from './constants';
import { getMerchantDetailsLink } from '@/utils/merchantTooingUrls';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getPredefinedCopySourceMid } from '@/flow/configs/utils/prefillOverwrite';
import { merchantRegion } from '@/selectors/merchant';

const tPrefix = 'products-fe.containers.confirm_copy';

export class ConfirmCopy extends React.Component {
  state = {
    isLoading: false,
    copyError: null,
  };

  onNext = async ({ confirmCopy }) => {
    const { onNext, mid, confirmMerchantCopy, region } = this.props;

    try {
      this.setState({
        copyError: null,
      });
      if (confirmCopy) {
        this.setState({ isLoading: true });
        await confirmMerchantCopy({ params: { mid }, region: region });
      }

      onNext({
        scrollBeforeNext: true,
        payload: { params: { mid } },
      });
    } catch (err) {
      this.setState({
        isLoading: false,
        copyError: err,
      });
    }
  };

  render() {
    const { t, error, region, environment, merchant, isDisabled, isLoading } = this.props;
    const copyFromMid = getPredefinedCopySourceMid(merchant);
    const link = getMerchantDetailsLink({
      mid: copyFromMid,
      environment,
      region,
      section: 'business',
    });

    return (
      <ContainerTemplate title={t(`${tPrefix}.title`)} error={error || this.state.copyError}>
        <SpacerVertical large />
        <Typography textToken="text-style/text/super-paragraphs/body/regular">
          {t(`${tPrefix}.disclaimer`)}{' '}
          {link ? (
            <Link target="_blank" isUnderlined href={link}>
              {t(`${tPrefix}.merchant_id`, { mid: copyFromMid })}
            </Link>
          ) : (
            t(`${tPrefix}.merchant_id`, { mid: copyFromMid })
          )}
        </Typography>
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          <SecondaryButton
            onClick={() => this.onNext({ confirmCopy: false })}
            disabled={this.state.isLoading || isDisabled}
            {...generateCyElemId(CY_CONFIRM_COPY_STEP_CONTAINER_IDS.BTN_SKIP_COPY, window)}
          >
            {t(`${tPrefix}.buttons.skipCopy`)}
          </SecondaryButton>
          <SpacerHorizontal medium />
          <CallToActionButton
            onClick={() => this.onNext({ confirmCopy: true })}
            disabled={this.state.isLoading || isDisabled}
            loading={this.state.isLoading || isLoading}
            {...generateCyElemId(CY_CONFIRM_COPY_STEP_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.confirmCopy`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

ConfirmCopy.propTypes = {
  ...defaultContainerPropTypes,
  confirmMerchantCopy: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  error: PropTypes.object,
};

export default compose(
  clientComponents.withPromiseBasedCapabilities({
    confirmMerchantCopy: CAPS.V2_MERCHANTS_CONFIRM_COPY,
  }),
  withCapabilityBeforeNext({
    getPreAssessmentData: CAPS.V2_PRE_ASSESSMENT_GET,
  }),
  connect(state => ({
    t: translator(state),
    environment: configSelectors.environment(state),
    region: merchantRegion(state),
  })),
)(ConfirmCopy);
